// Resize + compress images, or accept PDFs as-is.
// Returns { dataUri, kind: "image" | "pdf", filename }.

export async function fileToAttachment(file, { maxDim = 1400, quality = 0.82 } = {}) {
    if (!file) return null;
    const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");
    if (isPdf) {
        const dataUri = await readAsDataURL(file);
        return { dataUri, kind: "pdf", filename: file.name || "document.pdf" };
    }
    const dataUri = await fileToCompressedDataUri(file, { maxDim, quality });
    return { dataUri, kind: "image", filename: file.name || "photo.jpg" };
}

// Back-compat alias used by older callers
export async function fileToCompressedDataUri(file, { maxDim = 1400, quality = 0.82 } = {}) {
    if (!file) return null;
    const dataUrl = await readAsDataURL(file);
    const img = await loadImage(dataUrl);
    const { width: w0, height: h0 } = img;
    const scale = Math.min(1, maxDim / Math.max(w0, h0));
    const w = Math.round(w0 * scale);
    const h = Math.round(h0 * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL("image/jpeg", quality);
}

export function isPdfDataUri(dataUri) {
    return typeof dataUri === "string" && dataUri.startsWith("data:application/pdf");
}

/**
 * Open a PDF data-URI in a new tab — robust to Chrome's data:-URI top-frame
 * navigation block by converting to a Blob URL first, falling back to a
 * filename-suggested download when popups are blocked.
 *
 * Use this for any user-initiated PDF "view" action.
 */
export function openPdfDataUri(dataUri, filename = "document.pdf") {
    if (!isPdfDataUri(dataUri)) return false;
    try {
        // Strip header, base64-decode, build a Blob, hand out a Blob URL.
        const base64 = dataUri.split(",")[1] || "";
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const blob = new Blob([bytes], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const win = window.open(url, "_blank", "noopener,noreferrer");
        if (!win) {
            // Popup blocked → fall back to forced download.
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
        }
        // Release the Blob URL after the new tab has had time to load.
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        return true;
    } catch (e) {
        console.error("openPdfDataUri failed", e);
        return false;
    }
}

function readAsDataURL(file) {
    return new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onerror = () => reject(fr.error);
        fr.onload = () => resolve(fr.result);
        fr.readAsDataURL(file);
    });
}

function loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });
}
